import React, { useRef } from 'react';
import type { ReactNode } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { View } from 'react-native';
import { ActivityIndicator, RadioButton, TextInput } from 'react-native-paper';

import Text, { Title } from '@appComponents/Text';
import {
  TextFormField,
  FirstNameFormField,
  EmailFormField,
  LastNameFormField,
  PhoneNumberFormField,
} from '@appComponents/forms/FormFields';
import Button from '@appComponents/Button';
import styled from '@emotion/native';
import SearchField from '@appComponents/SearchField';
import PilotList from './PilotList';
import _ from 'lodash';
import Radio from '@appComponents/Radio';
import { RemoveButton } from '@appComponents/List';
import SelectOwner from './SelectOwner';
import { UserRole } from '@appUtils/tripConverter';
import { useTheme } from '@appComponents/theme';
import { Box, Spacer } from '@appComponents/ScreenLayout';
import { NumberField } from '@appComponents/forms/FormFields';
import UserRoleIndicator from '@appComponents/UserRoleIndicator';
import { useCompanyUsersFull } from '@appUtils/api';
import { userToPassengerData } from '@appUtils/passengers';

export const AircraftDetailsForm = ({
  control,
  watch,
}: AircraftDetailsProps): Node => {
  return (
    <FormLayout>
      <Field
        control={control}
        label="Tail #"
        name="tailNumber"
        rules={{
          required: 'Tail # is required',
        }}
      />

      <Field control={control} label="Aircraft Name" name="name" />

      <Field
        control={control}
        label="Aircraft Type"
        name="type"
        rules={{
          required: 'Aircraft Type is required',
        }}
      />

      <EnginesField control={control} />

      <Field
        control={control}
        label="Last Location"
        name="location"
        maxLength={4}
      />

      <FuelUnitsField control={control} />

      <MeteringSystemField control={control} />

      <APUMeteringSystemField control={control} />

      <CurrentFuelField control={control} watch={watch} />

      <PilotsField control={control} />
      <Spacer />
      <AircraftOwnersField control={control} />
    </FormLayout>
  );
};

export const AircraftOwnersField = ({ control }): ReactNode => {
  const searchRef = useRef(null);
  const theme = useTheme();

  return (
    <Controller
      control={control}
      name="owners"
      defaultValue={[]}
      render={({ field, fieldState }) => (
        <FormCol alignSelf="stretch">
          <Text size="large" weight={600}>
            Assigned Clients
          </Text>

          <SelectOwner
            innerRef={searchRef}
            isClearable
            isOptionDisabled={o => _.some(field.value, u => u.id === o.id)}
            onChange={option => {
              if (option) {
                field.onChange([...field.value, _.omit(option, ['roles'])]);
              }
            }}
            onBlur={e => {
              field.onBlur(e);
              searchRef.current.clearValue();
            }}
            style={searchFieldStyle}
            controlStyle={getControlStyle(theme)}
          />

          {fieldState.error && <Text>{fieldState.error.message}</Text>}

          <FormRow wrap="wrap" mt={1} alignItems="flex-start">
            {_.size(field.value) > 0 ? (
              <>
                <FormRow height="auto">
                  <Text weight={700}>Select a Primary Client</Text>
                </FormRow>

                {field.value.map(o => (
                  <OwnerItem
                    key={o.id}
                    checked={o === _.head(field.value)}
                    owner={o}
                    onCheck={() =>
                      field.onChange([o, ...field.value.filter(v => v !== o)])
                    }
                    onRemove={() =>
                      field.onChange(field.value.filter(v => v !== o))
                    }
                  />
                ))}
              </>
            ) : (
              <Text>Use the search field to add clients</Text>
            )}
          </FormRow>

          {/* Todo: This is removed for DEMOS */}
          {/*<FormDivider />*/}
          {/*<AddAircraftOwnerForm*/}
          {/*  onNewOwnerAdded={owner => field.onChange([...field.value, owner])}*/}
          {/*/>*/}
        </FormCol>
      )}
    />
  );
};

const AddAircraftOwnerForm = ({ onNewOwnerAdded }): ReactNode => {
  const { control, handleSubmit, reset } = useForm();

  const submitCallback = React.useCallback(
    payload => {
      const name = payload.firstName + ' ' + payload.lastName;

      // Todo: api call to save owner to management company and invite
      onNewOwnerAdded({ name });
      reset();
    },
    [onNewOwnerAdded, reset],
  );

  return (
    <FormCol alignSelf="stretch">
      <FormAltTitle>Add New Client</FormAltTitle>

      <FormRow>
        <FormCol mr={0.5}>
          <FirstNameFormField control={control} />
        </FormCol>
        <FormCol mr={0.5}>
          <LastNameFormField control={control} />
        </FormCol>
      </FormRow>
      <FormRow>
        <FormCol mr={0.5}>
          <EmailFormField control={control} />
        </FormCol>
        <FormCol mr={0.5}>
          <PhoneNumberFormField control={control} />
        </FormCol>
      </FormRow>
      <AddAndInviteButton
        color="primary"
        onPress={handleSubmit(submitCallback)}>
        Add & Invite
      </AddAndInviteButton>
    </FormCol>
  );
};

const EnginesField = ({ control }) => {
  const theme = useTheme();
  return (
    <FormRow>
      <FieldLabel>Number of Engines</FieldLabel>
      <FormCol flex={2}>
        <Controller
          control={control}
          name="numEngines"
          defaultValue={1}
          render={({ field, fieldState }) => (
            <>
              <SearchField
                options={[1, 2, 3].map(n => ({ value: n }))}
                defaultValue={{ value: field.value }}
                getOptionLabel={o => o.value}
                onChange={o => field.onChange(o.value)}
                onBlur={field.onBlur}
                style={searchFieldStyle}
                controlStyle={getControlStyle(theme)}
              />
              {fieldState.error && <Text>{fieldState.error.message}</Text>}
            </>
          )}
        />
      </FormCol>
    </FormRow>
  );
};

const PilotsField = ({ control }) => {
  const { data: pilots, loading } = useCompanyUsersFull({
    roles: [UserRole.PILOT, UserRole.MANAGER, UserRole.EMPLOYEE],
  });
  const options = pilots?.map(p => userToPassengerData({ user: p }));

  if (loading) {
    return <ActivityIndicator />;
  }

  return (
    <>
      <FormRow height="auto">
        <FieldLabel>Assigned Pilots</FieldLabel>
      </FormRow>
      <FormRow mt={0.5}>
        <FormCol ml={0.5} alignSelf="stretch">
          <Controller
            control={control}
            name="pilots"
            render={({ field, fieldState }) => (
              <>
                <PilotList
                  selected={field.value}
                  options={options}
                  onChange={field.onChange}
                  numColumns={1}
                  maxEntries={100}
                  showPIC={false}
                  showStatus={false}
                />
                {fieldState.error && <Text>{fieldState.error.message}</Text>}
                {_.isEmpty(field.value) && (
                  <Text>
                    Use the add button to assign some of your existing pilots
                    here
                  </Text>
                )}
              </>
            )}
          />
        </FormCol>
      </FormRow>
    </>
  );
};

const FUEL_UNITS_OPTIONS = [
  { value: 'gal', label: 'Gallons (gal)' },
  { value: 'lbs', label: 'Pounds (lbs)' },
  // { value: 'L', label: 'Liters (L)' },
  // { value: 'KG', label: 'Kilograms (KG)' },
];

const METERING_SYSTEM_OPTIONS = [
  { value: 'Hobbs', label: 'Hobbs' },
  { value: 'FMS', label: 'FMS' },
];

const APU_METERING_SYSTEM_OPTIONS = [
  { value: 'Hobbs', label: 'Hobbs' },
  { value: 'FMS', label: 'FMS' },
  { value: 'None', label: 'None' },
];

const CenteredRadio = styled(Radio)`
  margin-top: 7%;
`;

const FuelUnitsField = ({ control }) => {
  return (
    <RadioGroup
      control={control}
      label="Fuel Units"
      name="fuelUnits"
      options={FUEL_UNITS_OPTIONS}
    />
  );
};

const MeteringSystemField = ({ control }) => {
  return (
    <RadioGroup
      control={control}
      label="Metering System"
      name="meteringSystem"
      options={METERING_SYSTEM_OPTIONS}
    />
  );
};

const APUMeteringSystemField = ({ control }) => {
  return (
    <RadioGroup
      control={control}
      label="APU Metering System"
      name="apuMeteringSystem"
      options={APU_METERING_SYSTEM_OPTIONS}
    />
  );
};

const RadioGroup = ({ control, label, name, options, defaultValue }) => {
  return (
    <FormRow>
      <FieldLabel>{label}</FieldLabel>
      <FormCol flex={2}>
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange, value } }) => (
            <RadioButton.Group value={value} onValueChange={onChange}>
              <Box dir="row" jc="space-between" ai="center">
                {options.map(option => (
                  <CenteredRadio
                    key={option.value}
                    label={option.label}
                    value={option.value}
                  />
                ))}
              </Box>
            </RadioButton.Group>
          )}
        />
      </FormCol>
    </FormRow>
  );
};

const CurrentFuelField = ({ control, watch }) => {
  const fuelUnits = watch('fuelUnits');

  return (
    <FormRow>
      <FieldLabel>Current Fuel</FieldLabel>
      <FormCol flex={2}>
        <NumberField
          control={control}
          label="Current Fuel"
          name="fuelOff"
          decimalScale={1}
          right={<TextInput.Affix text={fuelUnits ?? 'gal'} />}
          labelCol={false}
        />
      </FormCol>
    </FormRow>
  );
};

const FieldLabel = ({ children }) => (
  <FormCol alignSelf="center">
    <Text size="large" weight="600">
      {children}
    </Text>
  </FormCol>
);

const Field = ({ label, control, name, ...rest }) => (
  <FormRow>
    <FieldLabel>{label}</FieldLabel>
    <FormCol flex={2}>
      <TextFormField
        light
        label={label}
        name={name}
        control={control}
        {...rest}
      />
    </FormCol>
  </FormRow>
);

const OwnerItem = ({ owner, onCheck, checked, onRemove }) => (
  <FormRow width="50%" height="auto">
    <Radio
      label={
        <Box dir="row" ai="center">
          {owner.name && <UserRoleIndicator user={owner} size={16} />}
          <Text size="mediumLarge">{owner.name}</Text>
        </Box>
      }
      value={owner.id}
      checked={checked}
      onChange={checked ? undefined : onCheck}
    />
    <RemoveButton onPress={onRemove} />
  </FormRow>
);

type AircraftDetailsProps = {
  control: Control,
  watch: Function,
};

const FormLayout = styled(View)(({ theme }) => ({
  flex: 1,
  marginBottom: theme.layout.space(1),
  width: '80%',
  alignSelf: 'center',
}));

const FormRow = styled.View`
  flex-direction: row;
  align-items: ${({ alignItems = 'center' }) => alignItems};
  justify-content: space-between;
  width: ${({ width = '100%' }) =>
    Number.isInteger(width) ? width + 'px' : width};
  min-height: ${({ height = 82 }) => height.toString()}px;
  flex-wrap: ${({ wrap }) => wrap};
  margin-top: ${({ theme, mt }) =>
    mt && `${theme.layout.space(mt).toString()}px`};
`;

const FormCol = styled.View(
  ({ theme, flex = 1, alignSelf = 'flex-start', mr, mh, ml }) => ({
    flexDirection: 'column',
    flex,
    alignSelf,
    marginHorizontal: mh && theme.layout.space(mh),
    marginRight: mr && theme.layout.space(mr),
    marginLeft: ml && theme.layout.space(ml),
  }),
);

const FormAltTitle = styled(Title)(({ theme }) => ({
  marginTop: theme.layout.gap(2),
  marginBottom: theme.layout.space(1),
}));

const searchFieldStyle = {
  marginTop: 22,
};

const getControlStyle = theme => ({
  backgroundColor: theme.colors.tableBackground,
  color: theme.colors.text,
  borderColor: theme.colors.fieldBorder,
});

const AddAndInviteButton = styled(Button)(({ theme }) => ({
  marginTop: theme.layout.space(0.5),
  marginLeft: theme.layout.gap(1),
  alignSelf: 'flex-start',
}));
